import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    ACCOUNTING_AREA_ID_MARKER,
    processRequestForAccountingAreaWithReplacement,
} from './network/managedRequestForAccountingArea';
import { API } from '../constants';
import { LoadLocation } from './locationsSlice';
import { pbRequestReset } from './pbRequestSlice';
import { PbRequest } from './pbRequest';
import { PbDispatch } from './index';

export enum SuggestedCostIncreaseAction {
    USE = 'USE',
    IGNORE = 'IGNORE',
}

export enum Schedule {
    IMMEDIATELY = 'IMMEDIATELY',
    IN_SEVEN_DAYS = 'IN_SEVEN_DAYS',
    FIRST_OF_NEXT_MONTH = 'FIRST_OF_NEXT_MONTH',
}

export interface LocationCostsDto extends BaseLocationCostsDto {
    vip_costs_enabled: boolean;
    laundry_room_name: string;
    laundry_room_id: number;
    currency_code: string;
    scheduled_costs?: UpcomingLocationCostsDto;
}

export interface BaseLocationCostsDto {
    use_vip_prices: boolean;
    price_vip_dryer?: number;
    price_normal_dryer: number;
    price_vip_washing_machine?: number;
    price_normal_washing_machine: number;
}

export interface SetLocationCostsDto extends BaseLocationCostsDto {
    schedule: Schedule;
}

export interface UpcomingLocationCostsDto extends BaseLocationCostsDto {
    due_time: number;
}

interface LocationCostsStore {
    locationCosts?: LocationCostsDto;
}

const initialState: LocationCostsStore = {
    locationCosts: undefined,
};

const locationCostsSlice = createSlice({
    name: 'locationCosts',
    initialState,
    reducers: {
        setLocationCosts(
            state: LocationCostsStore,
            action: PayloadAction<LocationCostsDto>
        ) {
            state.locationCosts = action.payload;
        },
    },
});

export function ResetSetLocationCosts() {
    return async (dispatch: PbDispatch) => {
        dispatch(pbRequestReset(PbRequest.SET_LOCATION_COSTS));
    };
}

export function LoadLocationCosts(locationId: number) {
    return async (dispatch: PbDispatch) => {
        try {
            const response =
                await processRequestForAccountingAreaWithReplacement<LocationCostsDto>(
                    dispatch,
                    `${API}/${ACCOUNTING_AREA_ID_MARKER}/locations/${locationId}/costs`,
                    { method: 'GET' },
                    PbRequest.GET_LOCATION_COSTS
                );
            dispatch(locationCostsSlice.actions.setLocationCosts(response));
        } catch {
            // already handled
        }
    };
}

export function SetLocationCosts(locationId: number, dto: SetLocationCostsDto) {
    return async (dispatch: PbDispatch) => {
        try {
            await processRequestForAccountingAreaWithReplacement(
                dispatch,
                `${API}/${ACCOUNTING_AREA_ID_MARKER}/locations/${locationId}/costs`,
                { method: 'PUT', body: JSON.stringify(dto) },
                PbRequest.SET_LOCATION_COSTS
            );
            dispatch(LoadLocationCosts(locationId));
            dispatch(LoadLocation(locationId));
        } catch {
            // already handled
        }
    };
}

export function UseSuggestedCostIncrease(
    locationId: number,
    action: SuggestedCostIncreaseAction,
    schedule: Schedule
) {
    return async (dispatch: PbDispatch) => {
        try {
            const request: RequestInit = {
                method: 'POST',
                body: JSON.stringify({ action, schedule }),
            };
            await processRequestForAccountingAreaWithReplacement(
                dispatch,
                `${API}/${ACCOUNTING_AREA_ID_MARKER}/locations/${locationId}/suggested_price_increase`,
                request,
                PbRequest.POST_USE_SUGGESTED_COST_INCREASE
            );

            await LoadLocationCosts(locationId)(dispatch);
            dispatch(LoadLocation(locationId));
        } catch {
            // already handled?
        }
    };
}

export function RevokeScheduledCostsChange(locationId: number) {
    return async (dispatch: PbDispatch) => {
        const request: RequestInit = {
            method: 'DELETE',
        };
        await processRequestForAccountingAreaWithReplacement(
            dispatch,
            `${API}/${ACCOUNTING_AREA_ID_MARKER}/locations/${locationId}/remove_scheduled_cost`,
            request,
            PbRequest.DELETE_REMOVE_SCHEDULED_COST
        );
        dispatch(LoadLocationCosts(locationId));
    };
}

export default locationCostsSlice.reducer;
